import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { RootState, AppDispatch } from '../../../src/redux/store';
import {
  excludeStaple,
  unexcludeStaple,
} from '../../../src/redux/userPreferencesSlice';
import { buildGroceryList } from '../../../src/redux/groceryListSlice';
import { STAPLE_GROUPS } from '../../../src/constants/staples';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
} from '../../../src/styles/theme';

export default function StaplesPreferencesScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();

  const excludedStaples = useSelector(
    (state: RootState) => state.userPreferences?.excludedStaples ?? []
  );

  const [localExcluded, setLocalExcluded] = useState<Set<string>>(
    new Set(excludedStaples)
  );
  const [isSaving, setIsSaving] = useState(false);

  const toggleStaple = useCallback((stapleKey: string) => {
    setLocalExcluded(prev => {
      const next = new Set(prev);
      if (next.has(stapleKey)) {
        next.delete(stapleKey);
      } else {
        next.add(stapleKey);
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      // Dispatch actions for removed and added exclusions
      const wasExcluded = new Set(excludedStaples);

      localExcluded.forEach(key => {
        if (!wasExcluded.has(key)) {
          dispatch(excludeStaple(key));
        }
      });

      excludedStaples.forEach(key => {
        if (!localExcluded.has(key)) {
          dispatch(unexcludeStaple(key));
        }
      });

      // Rebuild the grocery list with new exclusions
      dispatch(buildGroceryList());

      router.back();
    } finally {
      setIsSaving(false);
    }
  }, [excludedStaples, localExcluded, dispatch, router]);

  const isChanged = localExcluded.size !== excludedStaples.length ||
    Array.from(localExcluded).some(k => !excludedStaples.includes(k));

  return (
    <View style={styles.container}>
      {/* Nav Header */}
      <View style={styles.navHeader}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => router.back()}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Feather name="arrow-left" size={22} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.navTitle}>{t('settings.kitchenStaples')}</Text>
        {isChanged && !isSaving && (
          <TouchableOpacity
            style={styles.saveButton}
            onPress={handleSave}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Text style={styles.saveButtonText}>{t('common.save')}</Text>
          </TouchableOpacity>
        )}
        {isSaving && (
          <View style={styles.navSpacer}>
            <ActivityIndicator size="small" color={Paprika.primary} />
          </View>
        )}
        {!isChanged && !isSaving && <View style={styles.navSpacer} />}
      </View>

      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Text style={styles.description}>
          {t('settings.kitchenStaplesDesc')}
        </Text>

        {/* Staple Toggles */}
        <View style={styles.staplesList}>
          {STAPLE_GROUPS.map(group => {
            const isExcluded = localExcluded.has(group.key);
            const stapleLabel = t(`groceryList.staples.${group.key}`);

            return (
              <View key={group.key} style={styles.stapleRow}>
                <View style={styles.stapleInfo}>
                  <Text style={styles.stapleLabel}>{stapleLabel}</Text>
                </View>
                <TouchableOpacity
                  style={[styles.toggleSwitch, isExcluded && styles.toggleSwitchOff]}
                  onPress={() => toggleStaple(group.key)}
                  activeOpacity={0.7}
                >
                  <View
                    style={[
                      styles.toggleThumb,
                      isExcluded && styles.toggleThumbOff,
                    ]}
                  />
                </TouchableOpacity>
              </View>
            );
          })}
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  navHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.outline,
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  navTitle: {
    ...PaprikaTypography.titleMedium,
    color: Paprika.textPrimary,
    flex: 1,
    textAlign: 'center',
  },
  saveButton: {
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.xs,
  },
  saveButtonText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.primary,
    fontWeight: '600',
  },
  navSpacer: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scrollContent: {
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.md,
  },
  description: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textMuted,
    marginBottom: PaprikaSpacing.lg,
    lineHeight: 20,
  },
  staplesList: {
    gap: PaprikaSpacing.xs,
  },
  stapleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.sm,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.md,
    marginBottom: PaprikaSpacing.xs,
  },
  stapleInfo: {
    flex: 1,
  },
  stapleLabel: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
  },
  toggleSwitch: {
    width: 50,
    height: 28,
    borderRadius: 14,
    // Terracotta, not success green. DESIGN.md scopes primary to active
    // states and reserves the semantic colours for success/error/AI; an
    // "assumed in stock" toggle is an active state, not a success signal.
    backgroundColor: Paprika.primary,
    padding: 2,
    justifyContent: 'center',
  },
  toggleSwitchOff: {
    backgroundColor: Paprika.textMuted,
  },
  toggleThumb: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Paprika.surface,
    alignSelf: 'flex-end',
  },
  toggleThumbOff: {
    alignSelf: 'flex-start',
  },
});
